/**
 * i18n Configuration
 *
 * Configures internationalization with:
 * - Browser language detection
 * - Fallback to English
 * - Support for English and Simplified Chinese
 * - Multiple namespaces for organized translations
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";

// Language detection configuration
const languageDetectorOptions = {
  // Order of detection methods
  order: [
    "localStorage", // Check localStorage first (user preference)
    "navigator", // Then browser settings
    "htmlTag", // Then HTML lang attribute
    "path", // Then URL path
    "subdomain", // Finally subdomain
  ],

  // Keys for localStorage
  lookupLocalStorage: "i18nextLng",

  // Cache user language preference
  caches: ["localStorage"],

  // Don't cache on first visit (use detection)
  excludeCacheFor: ["cimode"],
};

// Supported namespaces (loaded on app init)
// Note: Sub-namespaces like 'products/tadreamk-eye' are loaded on-demand, not preloaded
const namespaces = [
  "common", // Header, footer, buttons, general UI
  "home", // Home page content
  "products", // Product descriptions (legacy)
  "aiservices", // AI Services page
  "about", // About Us page
  "careers", // Job listings
  "articles", // Magazine/news metadata (legacy)
  "business", // Business pages
  "auth", // Authentication pages
  "account", // User account pages
  "legal", // Privacy policy and terms of service
];

// Initialize i18next
i18n
  .use(Backend) // Load translations from files
  .use(LanguageDetector) // Detect user language
  .use(initReactI18next) // Pass i18n to react-i18next
  .init({
    // Supported languages - include English, Simplified Chinese, and Traditional Chinese
    supportedLngs: ["en", "zh", "zh-CN", "zh-TW"],

    // Fallback language configuration
    // Map all English variants to 'en', Simplified Chinese to 'zh-CN', Traditional Chinese to 'zh-TW'
    fallbackLng: {
      "en-US": ["en"],
      "en-GB": ["en"],
      "en-AU": ["en"],
      "en-CA": ["en"],
      zh: ["zh-CN"],
      "zh-Hans": ["zh-CN"],
      "zh-Hant": ["zh-TW"],
      "zh-TW": ["zh-TW"],
      "zh-HK": ["zh-TW"],
      default: ["en"],
    },

    // Don't use languageOnly - we need to support zh-CN with region code
    load: "all",

    // Allow non-explicit supported languages (like zh -> zh-CN)
    nonExplicitSupportedLngs: true,

    // Default namespace
    defaultNS: "common",

    // Namespaces to load (others can be loaded on-demand)
    ns: namespaces,

    // Allow loading namespaces that aren't in the initial ns array
    partialBundledLanguages: true,

    // Language detection configuration
    detection: languageDetectorOptions,

    // Backend configuration (file loading)
    backend: {
      // Path to translation files with fallback handling
      loadPath: (lngs, namespaces) => {
        let lng = lngs[0];
        const ns = namespaces[0];

        // Normalize language code to match our folder structure
        // We have 'en', 'zh-CN', and 'zh-TW' folders
        if (lng === "zh-TW" || lng === "zh-Hant" || lng === "zh-HK") {
          lng = "zh-TW";
        } else if (lng === "zh" || lng === "zh-CN" || lng === "zh-Hans") {
          lng = "zh-CN";
        } else if (lng === "en" || lng.startsWith("en-")) {
          lng = "en";
        } else {
          // Default to English for unsupported languages
          lng = "en";
        }

        const path = `/locales/${lng}/${ns}.json`;
        return path;
      },

      // Add error handling
      requestOptions: {
        mode: "cors",
        credentials: "same-origin",
        cache: "default",
      },
    },

    // Debug mode (set to false in production)
    debug: false,

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React configuration
    react: {
      useSuspense: false, // Disable Suspense to avoid loading issues
    },

    // Fallback for missing translations
    fallbackNS: "common",
  });

// Log when loading fails
i18n.on("failedLoading", (lng, ns, msg) => {
  console.error("[i18n] Failed to load:", { lng, ns, msg });
});

export default i18n;
